import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const PageShell = ({ title, kicker, children }) => (
  <div className="min-h-screen bg-[#040914] bg-tech-grid">
    <div className="max-w-3xl mx-auto px-6 py-14">
      <div className="flex items-center justify-between mb-12">
        <Link to="/" className="flex items-center gap-2.5">
          <ShieldCheck className="w-6 h-6 text-cyan-neon" />
          <span className="font-display text-lg text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-cyan-neon mb-3">{kicker}</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight mb-10">{title}</h1>
        <div className="space-y-8">{children}</div>
      </motion.div>
      <p className="mt-16 pt-8 border-t border-white/8 font-mono text-xs text-slate-600">© 2026 ClickShield AI. All rights reserved.</p>
    </div>
  </div>
);

const Section = ({ h, children }) => (
  <section>
    <h2 className="font-display text-lg text-white mb-3">{h}</h2>
    <div className="text-[15px] text-slate-400 font-light leading-relaxed space-y-3">{children}</div>
  </section>
);

export function About() {
  return (
    <PageShell kicker="Company" title="We hate wasted ad spend as much as you do.">
      <Section h="Our mission">
        <p>Every year, advertisers lose tens of billions of dollars to click fraud — bots, click farms, competitors and bad actors draining budgets one fake click at a time. ClickShield AI exists to give that money back to the businesses that earned it.</p>
        <p>We built a real-time detection engine that fingerprints every visit to your landing pages, scores it against dozens of fraud signals — headless browsers, datacenter IPs, click velocity, proxy networks — and stops confirmed bots before they cost you a cent.</p>
      </Section>
      <Section h="What we believe">
        <p><span className="text-white">Protection should be instant.</span> One line of JavaScript, live in under 60 seconds — no SDKs, no engineering sprints.</p>
        <p><span className="text-white">Data should be yours.</span> Your traffic data is used to protect your campaigns and nothing else. We never sell or share it.</p>
        <p><span className="text-white">Transparency wins.</span> Every blocked click is logged with the exact reason it was stopped, and you can share a public shield-status page with your clients.</p>
      </Section>
      <Section h="Get in touch">
        <p>Questions, feedback or partnership ideas? <Link to="/contact" className="text-cyan-neon hover:underline">Contact us</Link> — a human replies within one business day.</p>
      </Section>
    </PageShell>
  );
}

export function Privacy() {
  return (
    <PageShell kicker="Legal" title="Privacy Policy">
      <p className="font-mono text-xs text-slate-500">Last updated: June 2026</p>
      <Section h="1. What we collect">
        <p><span className="text-white">Account data:</span> your name, email address and hashed password when you register, plus billing details processed securely by Stripe (we never store card numbers).</p>
        <p><span className="text-white">Traffic data:</span> when you install our tracking snippet, we process technical signals from visits to your pages — IP address, user agent, screen size, language, timezone and ad-click identifiers — solely to score visits for fraud.</p>
      </Section>
      <Section h="2. How we use it">
        <p>We use collected data to detect and block fraudulent clicks, generate your dashboards and reports, send emails you've opted into (weekly reports, threat alerts, password resets) and operate billing. We do not sell personal data, ever.</p>
      </Section>
      <Section h="3. Data retention & deletion">
        <p>Click events are retained for 12 months for reporting, then aggregated. You can request deletion of your account and all associated data at any time via the contact page — we complete deletion requests within 30 days.</p>
      </Section>
      <Section h="4. Cookies">
        <p>The dashboard uses a single, essential httpOnly authentication cookie. Our marketing site sets no advertising or cross-site tracking cookies.</p>
      </Section>
      <Section h="5. Your rights">
        <p>Under GDPR and similar regulations you may request access, correction, portability or erasure of your personal data. Email us via the <Link to="/contact" className="text-cyan-neon hover:underline">contact page</Link> to exercise these rights.</p>
      </Section>
      <Section h="6. Subprocessors">
        <p>We rely on a small set of vetted providers: cloud hosting for infrastructure, Stripe for payments and a transactional email service for report and alert delivery. Each is bound by data-processing agreements.</p>
      </Section>
    </PageShell>
  );
}

export function Terms() {
  return (
    <PageShell kicker="Legal" title="Terms of Service">
      <p className="font-mono text-xs text-slate-500">Last updated: June 2026</p>
      <Section h="1. The service">
        <p>ClickShield AI provides click-fraud detection and blocking tools for online advertisers. By creating an account you agree to these terms on behalf of yourself or the business you represent.</p>
      </Section>
      <Section h="2. Subscriptions & billing">
        <p>Paid plans renew automatically (monthly or annually) until cancelled. You can upgrade, downgrade or cancel at any time from Billing &amp; Reports; cancellations take effect at the end of the current billing period. Fees are non-refundable except where required by law.</p>
      </Section>
      <Section h="3. Acceptable use">
        <p>You may only install the tracking snippet on websites you own or are authorised to protect. You must not use the service to collect data unlawfully, to attack or disrupt third parties, or to reverse-engineer our detection systems.</p>
      </Section>
      <Section h="4. Service levels">
        <p>We target 99.9% availability for the tracking endpoint. Fraud detection is probabilistic: we make no guarantee that every fraudulent click is blocked or that no legitimate visit is ever flagged, and we are not liable for ad-platform billing decisions.</p>
      </Section>
      <Section h="5. Limitation of liability">
        <p>To the maximum extent permitted by law, ClickShield AI's total liability under these terms is limited to the fees you paid in the twelve months preceding the claim. We are not liable for indirect or consequential damages, including lost profits or ad spend.</p>
      </Section>
      <Section h="6. Termination & changes">
        <p>You may delete your account at any time. We may suspend accounts that violate these terms. We may update these terms with 14 days' notice by email; continued use after the effective date constitutes acceptance.</p>
      </Section>
    </PageShell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = "mt-2 w-full px-4 py-3 rounded-lg bg-[#0B101A] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors";

  return (
    <PageShell kicker="Support" title="Talk to a human.">
      {sent ? (
        <div data-testid="contact-success" className="rounded-2xl neon-border bg-[#0B101A]/80 p-10 text-center">
          <MailCheck className="w-10 h-10 text-[#39FF14] mx-auto mb-4" />
          <h2 className="font-display text-2xl text-white mb-2">Message received</h2>
          <p className="text-sm text-slate-400">Thanks {form.name.split(" ")[0]} — we'll reply to {form.email} within one business day.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl neon-border bg-[#0B101A]/60 p-8 space-y-4 max-w-xl">
          <p className="text-sm text-slate-400 font-light">Questions about plans, protection or partnerships — we reply within one business day.</p>
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Name</label>
            <input data-testid="contact-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Advertiser" className={field} />
          </div>
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Email</label>
            <input data-testid="contact-email-input" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className={field} />
          </div>
          <div>
            <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Message</label>
            <textarea data-testid="contact-message-input" required minLength={5} rows={5} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" className={field} />
          </div>
          {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="contact-submit-btn" type="submit" disabled={loading}
            className="w-full py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:glow-cyan transition-shadow inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}
          </button>
        </form>
      )}
    </PageShell>
  );
}
